import { useEffect, useMemo, useRef, useState } from 'react';
import { search as dbSearch, OBJECTS, list, BACKEND } from '../lib/db';
import './palette.css';

/* The command palette is the primary navigation surface, not a search box bolted on.
   It resolves modules, records and actions from one input, which is what allows the
   navigation tree to become optional rather than load-bearing. */

interface Item {
  id: string;
  title: string;
  subtitle?: string;
  group: string;
  path: string;
  badge?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
  modules: { label: string; path: string }[];
}

/* Subsequence match, the behaviour people expect from a palette: "bolsor" finds
   "Bologna S. Orsola". Returns a score so better matches float up. */
function fuzzy(needle: string, haystack: string): number | null {
  if (!needle) return 0;
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();
  const direct = h.indexOf(n);
  if (direct === 0) return 1000;
  if (direct > 0) return 700 - direct;

  let i = 0, score = 0, streak = 0;
  for (let j = 0; j < h.length && i < n.length; j++) {
    if (h[j] === n[i]) {
      streak += 1;
      score += streak * 2 + (j === 0 || h[j - 1] === ' ' ? 6 : 0);
      i += 1;
    } else streak = 0;
  }
  return i === n.length ? score : null;
}

export function CommandPalette({ open, onClose, onNavigate, modules }: Props) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setQ('');
      setSel(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  /* Default view: modules and actions. Typing hands over to db.search, which covers every
     object type registered in OBJECTS without the palette knowing what they are. */
  const defaults = useMemo<Item[]>(() => {
    const out: Item[] = modules.map((m) => ({
      id: `mod-${m.path}`, title: m.label, group: 'Go to', path: m.path,
    }));
    for (const def of Object.values(OBJECTS)) {
      const n = list(def.key).length;
      if (n) {
        out.push({
          id: `all-${def.key}`, title: def.plural, subtitle: `${n.toLocaleString()} records`,
          group: 'Browse', path: def.route(''),
        });
      }
    }
    ['New opportunity', 'Log interaction', 'Capture intelligence', 'New contract', 'Raise invoice']
      .forEach((a, i) => out.push({ id: `a-${i}`, title: a, group: 'Actions', path: '/pipeline' }));
    return out;
  }, [modules]);

  /* The document estate, searched live. Everything else in the palette is synchronous over
     the compiled seed; documents only exist in Postgres, so this one group arrives a beat
     later, debounced, and only when a backend exists. */
  const [docHits, setDocHits] = useState<Item[]>([]);
  useEffect(() => {
    if (BACKEND !== 'supabase' || q.trim().length < 2) { setDocHits([]); return; }
    const stop = setTimeout(async () => {
      try {
        const { supabase } = await import('../lib/supabase');
        const { data } = await supabase().schema('app')
          .rpc('search_documents', { p_q: q.trim(), p_limit: 8 });
        setDocHits(((data ?? []) as { out_kind: string; out_id: string; out_title: string;
          out_snippet: string; out_url: string }[]).map((d) => ({
            id: `doc-${d.out_id}`, title: d.out_title, subtitle: `${d.out_kind}${d.out_snippet ? ' · ' + d.out_snippet : ''}`,
            group: 'Documents', path: d.out_url,
          })));
      } catch { setDocHits([]); }
    }, 250);
    return () => clearTimeout(stop);
  }, [q]);

  const results = useMemo(() => {
    if (!q.trim()) return defaults.slice(0, 14);
    const hits = dbSearch(q, 24).map<Item>((h) => ({
      id: `${h.object}-${h.id}`,
      title: h.title,
      subtitle: h.subtitle,
      group: OBJECTS[h.object]?.plural ?? h.object,
      path: h.route,
    }));
    const local = defaults
      .map((i) => ({ i, s: fuzzy(q, i.title) ?? -1 }))
      .filter((r) => r.s >= 0)
      .sort((a, b) => b.s - a.s)
      .map((r) => r.i);
    return [...local.slice(0, 6), ...hits, ...docHits].slice(0, 30);
  }, [q, defaults, docHits]);

  useEffect(() => setSel(0), [q]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
      if (e.key === 'Enter' && results[sel]) { e.preventDefault(); onNavigate(results[sel].path); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, results, sel, onNavigate]);

  useEffect(() => {
    listRef.current?.querySelector('[data-sel="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  if (!open) return null;

  let lastGroup = '';

  return (
    <div className="cmdk-scrim" onMouseDown={onClose}>
      <div className="cmdk" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="Command palette">
        <div className="cmdk-input-row">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
            <circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5L21 21" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search centres, countries, routes, or type a command…"
            aria-label="Search"
            spellCheck={false}
          />
          <kbd>esc</kbd>
        </div>

        <div className="cmdk-list" ref={listRef}>
          {results.length === 0 && (
            <div className="cmdk-empty">
              No matches for <strong>{q}</strong>
            </div>
          )}
          {results.map((r, idx) => {
            const showGroup = r.group !== lastGroup;
            lastGroup = r.group;
            return (
              <div key={r.id}>
                {showGroup && <div className="cmdk-group">{r.group}</div>}
                <button
                  className={`cmdk-item ${idx === sel ? 'sel' : ''}`}
                  data-sel={idx === sel}
                  onMouseEnter={() => setSel(idx)}
                  onClick={() => onNavigate(r.path)}
                >
                  <div className="cmdk-item-main">
                    <span className="cmdk-title">{r.title}</span>
                    {r.subtitle && <span className="cmdk-sub">{r.subtitle}</span>}
                  </div>
                  {r.badge && <span className="pill mute">{r.badge}</span>}
                </button>
              </div>
            );
          })}
        </div>

        <div className="cmdk-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span className="cmdk-foot-right">{results.length} result{results.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}
